<template>
  <vue-draggable-resizable
    class="ql-script-marker"
    :class="[
      `ql-script-marker-${markerTypeClass}`,
      {
        'ql-script-marker-locked': locked || !editable,
        'ql-script-marker-editing': editing || inlineEditing,
        'ql-script-marker--multiline': isMultiline,
        'show-md-dark-mode': showMode
      }
    ]"
    :key="handler"
    :handles="[]"
    :style="markerStyle"
    :resizable="false"
    @mousedown.native="selectForCopy"
    :draggable="editable && !locked && !inlineEditing"
    :prevent-deactivation="inlineEditing || keepEditing"
    drag-cancel=".ql-script-marker-actions"
    :x="posLeft"
    :y="posTop"
    :w="markerWidth"
    :h="markerHeight"
    :parent="true"
    @dragging="onDrag"
    @dragstop="onDragStop"
    @activated="onActivate"
    @deactivated="onDeactivate"
  >
    <div class="ql-script-marker-body">
      <!-- Type glyph for songs: the ♪ CHARACTER, not the SVG note.
           The bookmark icon's note is a solid filled path with a heavy beam, so
           at the same height it reads far weightier than the text beside it and
           dominates a short title. The character is the same idea drawn lightly,
           and it inherits font, colour and zoom for free.
           A SIBLING of the label, not inside it: box width is measured from the
           text alone (measureLabelWidth), so a glyph within the measured span
           would be invisible to the prediction and the box would clip until the
           observed width caught up. Its width is added to the chrome instead. -->
      <span
        v-if="marker.type === 'song'"
        class="ql-script-marker-type-glyph"
        aria-label="Song"
      >&#9834;</span>
      <template v-if="inlineEditing">
        <input
          ref="inlineInput"
          class="ql-script-marker-inline-input"
          v-model="editLabel"
          @keydown="onInlineKeydown"
          @blur="commitInlineEdit"
          @click.stop
          @mousedown.stop
        />
      </template>
      <span
        v-else
        ref="label"
        class="ql-script-marker-label"
        :title="tooltipText"
        @click.stop="selectMarker"
      >{{ displayLabel }}</span>
    </div>
    <div class="ql-script-marker-actions" v-if="editing && editable && !locked && !inlineEditing">
      <span
        class="ql-script-marker-action"
        @mousedown.stop.prevent="onMarkerEditClick"
        @click.stop.prevent="onMarkerEditClick"
      >
        <md-icon>edit</md-icon>
        <md-tooltip>Edit</md-tooltip>
      </span>
      <span
        class="ql-script-marker-action"
        @mousedown.stop.prevent="onMarkerDeleteClick"
        @click.stop.prevent="onMarkerDeleteClick"
      >
        <md-icon>clear</md-icon>
        <md-tooltip>Delete</md-tooltip>
      </span>
    </div>
    <md-dialog-confirm
      md-title="Delete marker"
      :md-content-html="deleteConfirmHtml"
      md-ok-text="Delete"
      md-cancel-text="Cancel"
      @close="onDeleteConfirm"
      ref="deleteDialog"
    />
  </vue-draggable-resizable>
</template>

<script>
import { mapGetters } from 'vuex';
import { escapeHtml } from '@/lib/escapeHtml';
import vueDragResize from 'vue-draggable-resizable';
import NavBus from '@/buses/nav';
import {
    defaultMarkerLabel,
    markerDisplayLabel,
    MARKER_TYPE_PREFIX
} from '@/lib/marker-labels';
import { MARKER_TYPE_COLORS } from '@/lib/structureOrder';

// Base sizes designed at 100% zoom; multiply by selectedScale so markers
// grow/shrink with the page the same way cues and notes do.
const BASE_FONT = 11;
// Fallback only. Real width comes from measureLabelWidth() below; this average
// is used just for the multiline line-count estimate and if canvas is missing.
const BASE_CHAR_WIDTH = 8;
const BASE_HEIGHT = 24;
const BASE_INLINE_HEIGHT = 28;
const BASE_MIN_WIDTH = 64;
const BASE_INLINE_MIN_WIDTH = 90;
const BASE_PADDING_X = 6;
const BASE_PADDING_Y = 2;
const BASE_BORDER_RADIUS = 4;
const BASE_LINE_HEIGHT = 14;
const BASE_MAX_MULTILINE_HEIGHT = 100;
// Sub-pixel slack so a glyph never lands a hair outside the measured box.
const BASE_LABEL_SAFETY_PAD = 4;
// The song note glyph and its gap. Added to the chrome rather than measured,
// because the glyph sits OUTSIDE the span the width is measured from.
// The ♪ character at 1.1em of an 11px base, plus a 3px gap.
const BASE_TYPE_ICON_W = 10;

/*
 * Measure the label the way it actually renders instead of assuming a fixed
 * average character width. A flat 8px/char overshoots 11px semibold Roboto by
 * roughly a third; that is invisible on short labels (they sit on the
 * BASE_MIN_WIDTH floor) but leaves a large empty run to the right of long ones.
 *
 * Measured with a hidden DOM span rather than canvas measureText. The app loads
 * Roboto 400 only (see styles/extra.scss) while the label renders at
 * font-weight 600, so the browser SYNTHESIZES the semibold. Canvas reports the
 * regular-weight advance for a synthesized face while the DOM paints the
 * emboldened one — narrower than reality, and the error compounds per glyph,
 * which clipped long labels. A DOM span goes through the same text engine that
 * paints, so synthetic bold, letter-spacing, text-transform and font fallback
 * are all accounted for.
 */
let measureEl = null;

function getMeasureEl() {
    if (!measureEl && typeof document !== 'undefined' && document.body) {
        measureEl = document.createElement('span');
        measureEl.setAttribute('aria-hidden', 'true');
        const s = measureEl.style;
        s.position = 'absolute';
        s.top = '-9999px';
        s.left = '-9999px';
        s.visibility = 'hidden';
        s.pointerEvents = 'none';
        s.whiteSpace = 'pre';
        document.body.appendChild(measureEl);
    }
    return measureEl;
}

/**
 * @param text   the exact string the marker will paint
 * @param sample a live element whose computed font the label inherits
 */
function measureLabelWidth(text, sample) {
    if (!text || !sample || typeof window === 'undefined' || !window.getComputedStyle) {
        return 0;
    }
    const el = getMeasureEl();
    if (!el) {
        return 0;
    }
    const cs = window.getComputedStyle(sample);
    el.style.fontFamily = cs.fontFamily;
    el.style.fontSize = cs.fontSize;
    el.style.fontWeight = cs.fontWeight;
    el.style.fontStyle = cs.fontStyle;
    el.style.letterSpacing = cs.letterSpacing;
    el.style.textTransform = cs.textTransform;
    el.textContent = text;
    return el.getBoundingClientRect().width;
}

export default {
    name: 'ScriptMarkerOverlay',
    components: {
        'vue-draggable-resizable': vueDragResize
    },
    props: {
        marker: { type: Object, required: true },
        locked: { type: Boolean, default: false },
        editable: { type: Boolean, default: true },
        pageWidth: { type: Number, required: true },
        pageHeight: { type: Number, required: true },
        startInlineEdit: { type: Boolean, default: false },
        // Print draws the page at its own scale ('page-print-scale'), not the editor zoom, and a print window never
        // sets the store zoom. Print passes its scale here so the mark is sized like the page around it.
        scale: { type: Number, default: null }
    },
    data() {
        return {
            dragging: false,
            editing: false,
            inlineEditing: false,
            editLabel: '',
            keepEditing: false,
            suppressBlurCommit: false,
            handler: 0,
            // Bumped when the label becomes measurable ($el exists) and again
            // once webfonts settle, so widths get recomputed. See
            // measureLabelWidth().
            measureRevision: 0,
            // Width the browser actually needed for the painted label, read
            // back from the DOM after render. Zero until observed.
            observedTextWidth: 0,
            // Guards the async fonts.ready remeasure against a marker that has
            // already been torn down (page change, project close).
            alive: true
        };
    },
    mounted() {
        // $el now exists, so labelNaturalWidth can read the real computed font
        // instead of the pre-mount fallback estimate.
        this.remeasure();
        if (typeof document !== 'undefined' && document.fonts && document.fonts.ready
            && typeof document.fonts.ready.then === 'function') {
            document.fonts.ready.then(this.remeasure).catch(() => {
                // Font loading API unavailable or rejected — the measurement
                // already in place stays good enough.
            });
        }
    },
    watch: {
        'marker.x': function() {
            if (!this.dragging) {
                this.handler += 1;
            }
        },
        'marker.y': function() {
            if (!this.dragging) {
                this.handler += 1;
            }
        },
        pageWidth: function() {
            if (!this.dragging) {
                this.handler += 1;
            }
        },
        pageHeight: function() {
            if (!this.dragging) {
                this.handler += 1;
            }
        },
        selectedScale: function() {
            if (!this.dragging) {
                this.remeasure();
            }
        },
        scale: function() {
            if (!this.dragging) {
                this.remeasure();
            }
        },
        startInlineEdit: {
            immediate: true,
            handler(shouldStart) {
                if (shouldStart && this.editable && !this.locked) {
                    this.$nextTick(() => this.beginInlineEdit());
                }
            }
        },
        editing(isEditing) {
            if (isEditing) {
                document.addEventListener('mousedown', this.onDocumentPointerDown, true);
            } else {
                document.removeEventListener('mousedown', this.onDocumentPointerDown, true);
            }
        },
        'marker.label'() {
            if (!this.dragging && !this.inlineEditing) {
                this.remeasure();
            }
        },
        // Leaving inline edit re-renders the plain label; re-observe then, since
        // the label watcher is skipped while inlineEditing is true.
        inlineEditing(isEditing) {
            if (!isEditing) {
                this.remeasure();
            }
        }
    },
    computed: {
        /**
         * ⚠ NEVER let this return an empty string. vue-material's
         * md-dialog-confirm THROWS from its own mounted hook when both
         * md-content and md-content-html are falsy:
         *
         *   mounted() { if (!this.mdContent && !this.mdContentHtml) throw ... }
         *
         * The dialog is rendered inline rather than behind a v-if, so it mounts
         * with every marker. As a data key seeded with '' it therefore threw
         * ONCE PER MARKER ON THE PAGE, every page load — a wall of console noise
         * that had nothing to do with anything and made real bug reports
         * unreadable. It is what buried the signal in report LBqL6zmD2jgRtKwAIK3A.
         *
         * As a computed it is derived from the marker, so it is correct before
         * the dialog is ever opened and cannot be empty. The label is escaped
         * because this is a raw-HTML sink and labels are user-typed.
         */
        deleteConfirmHtml() {
            const label = this.marker && this.marker.label;
            return label
                ? `Delete <strong>${escapeHtml(label)}</strong>?`
                : 'Delete this marker?';
        },
        ...mapGetters(['showMode', 'selectedScale']),
        zoomScale() {
            const scale = Number(this.scale != null ? this.scale : this.selectedScale);
            return Number.isFinite(scale) && scale > 0 ? scale : 1;
        },
        markerTypeClass() {
            return this.marker.type || 'scene';
        },
        fixedPrefix() {
            return MARKER_TYPE_PREFIX[this.marker.type] || '';
        },
        // Legacy shim: strips a stored "Song " from marks made before the glyph.
        displayLabel() {
            return markerDisplayLabel(this.marker);
        },
        tooltipText() {
            if (!this.editable || this.locked) {
                return `${this.marker.type}: ${this.marker.label}`;
            }
            return `Click to edit - drag to move - ${this.marker.type}: ${this.marker.label}`;
        },
        // Keep marker inside the page: width cannot exceed remaining space from x.
        maxMarkerWidth() {
            const remaining = this.pageWidth - this.posLeft - (8 * this.zoomScale);
            return Math.max(BASE_MIN_WIDTH * this.zoomScale, remaining);
        },
        // The exact string being rendered, inline-edit prefix included.
        measuredText() {
            // The displayed text, not the stored one: a legacy song still holding
            // "Song X" renders as "X", so measuring the raw label would size the
            // box for a word that is not drawn.
            return this.inlineEditing ? (this.editLabel || '') : this.displayLabel;
        },
        // Hugs the rendered text. `measureRevision` is a reactive dependency,
        // bumped after mount (once $el exists to read the real font from) and
        // again once webfonts settle, so an early estimate gets corrected.
        labelNaturalWidth() {
            const text = this.measuredText;
            const s = this.zoomScale;
            const minW = (this.inlineEditing ? BASE_INLINE_MIN_WIDTH : BASE_MIN_WIDTH) * s;
            // Predicted width, and the width the browser actually needed for
            // the painted text. The observed value wins when it is larger —
            // that is the guarantee against clipping, whatever the prediction
            // got wrong (font not yet loaded, synthetic weight, letter-spacing).
            const predicted = this.measureRevision >= 0
                ? measureLabelWidth(text, this.$el)
                : 0;
            const measured = Math.max(predicted, this.observedTextWidth);
            if (measured > 0) {
                // Box padding both sides, a sub-pixel guard, and a little extra
                // room for the caret while inline editing. `measured` already
                // carries the zoom (it comes off the live, scaled element), so
                // only the chrome is scaled here.
                const chrome = ((BASE_PADDING_X * 2) + BASE_LABEL_SAFETY_PAD
                    + (this.marker.type === 'song' ? BASE_TYPE_ICON_W : 0)
                    + (this.inlineEditing ? 6 : 0)) * s;
                return Math.max(minW, Math.ceil(measured + chrome));
            }
            // Not measurable yet (pre-mount) — fall back to the old estimate.
            const padding = ((this.inlineEditing ? 10 : 8)
                + (this.marker.type === 'song' ? BASE_TYPE_ICON_W : 0)) * s;
            const charW = BASE_CHAR_WIDTH * s;
            return Math.max(minW, Math.ceil(text.length * charW) + padding);
        },
        markerWidth() {
            return Math.min(this.labelNaturalWidth, this.maxMarkerWidth);
        },
        isMultiline() {
            if (this.inlineEditing) {
                return false;
            }
            return this.labelNaturalWidth > this.maxMarkerWidth;
        },
        markerHeight() {
            if (this.inlineEditing) {
                return BASE_INLINE_HEIGHT * this.zoomScale;
            }
            if (!this.isMultiline) {
                return BASE_HEIGHT * this.zoomScale;
            }
            const text = this.marker.label || '';
            const s = this.zoomScale;
            // Derive the average glyph width from the same measurement the box
            // uses, so the line count matches how the text actually wraps.
            const measured = this.measureRevision >= 0 ? measureLabelWidth(text, this.$el) : 0;
            const charW = (measured > 0 && text.length)
                ? (measured / text.length)
                : BASE_CHAR_WIDTH * s;
            const charsPerLine = Math.max(8, Math.floor((this.markerWidth - (12 * s)) / charW));
            const lines = Math.max(1, Math.ceil(text.length / charsPerLine));
            return Math.min(
                BASE_MAX_MULTILINE_HEIGHT * this.zoomScale,
                (4 + (lines * BASE_LINE_HEIGHT)) * this.zoomScale
            );
        },
        posLeft() {
            if (!this.dragging && this.marker.x <= 1) {
                return this.marker.x * this.pageWidth;
            }
            return this.marker.x;
        },
        posTop() {
            if (!this.dragging && this.marker.y <= 1) {
                return this.marker.y * this.pageHeight;
            }
            return this.marker.y;
        },
        markerStyle() {
            const type = this.marker.type || 'scene';
            // Type colour is the whole point of Act / Scene / Song marks, so it
            // holds in dark mode too. Staging flattened every type to #424242
            // when showMode was on, which removed the distinction at a glance
            // and disagreed with the Manage structure dialog, whose type badges
            // stay coloured in dark mode.
            //
            // The palette lives in structureOrder.js so this mark and the list
            // view's section headers cannot drift apart.
            const swatch = MARKER_TYPE_COLORS[type] || MARKER_TYPE_COLORS.scene;
            const backgroundColor = swatch.bg;
            // Song's fill is light enough to need dark text; the other two are
            // mid-tone and take white, in both modes.
            const textColor = swatch.fg;
            const s = this.zoomScale;
            return {
                backgroundColor,
                color: textColor,
                width: `${this.markerWidth}px`,
                minWidth: `${Math.min(BASE_MIN_WIDTH * s, this.markerWidth)}px`,
                height: `${this.markerHeight}px`,
                fontSize: `${BASE_FONT * s}px`,
                padding: `${BASE_PADDING_Y * s}px ${BASE_PADDING_X * s}px`,
                borderRadius: `${BASE_BORDER_RADIUS * s}px`,
                '--script-marker-scale': s
            };
        }
    },
    beforeDestroy() {
        this.alive = false;
        document.removeEventListener('mousedown', this.onDocumentPointerDown, true);
    },
    methods: {
        // Force labelNaturalWidth / markerHeight to recompute, and re-key the
        // draggable wrapper so it picks up the new width.
        remeasure() {
            if (!this.alive) {
                return;
            }
            // Drop the previous observation so the box can shrink as well as
            // grow when the label or zoom changes.
            this.observedTextWidth = 0;
            this.measureRevision += 1;
            this.handler += 1;
            this.syncObservedWidth();
        },
        /*
         * Read back the width the browser actually needed. `.ql-script-marker-label`
         * is `white-space: nowrap; overflow: hidden`, so scrollWidth is the full
         * text width even while it is visually clipped. If that exceeds what the
         * box currently gives the text, adopt it — one pass converges, because
         * scrollWidth does not change when the box grows.
         */
        syncObservedWidth() {
            this.$nextTick(() => {
                if (!this.alive || this.inlineEditing || this.isMultiline) {
                    return;
                }
                const el = this.$refs.label;
                if (!el || !el.scrollWidth) {
                    return;
                }
                const needed = Math.ceil(el.scrollWidth);
                if (needed > this.observedTextWidth && needed > el.clientWidth) {
                    this.observedTextWidth = needed;
                    this.handler += 1;
                }
            });
        },
        beginInlineEdit() {
            this.inlineEditing = true;
            this.editing = true;
            // markerDisplayLabel already removes a legacy stored "Song ", so
            // editing an old song mark puts the title in the field and drops the
            // word on save — the data heals itself as people touch it.
            this.editLabel = this.displayLabel || defaultMarkerLabel(this.marker.type);
            this.$nextTick(() => {
                window.setTimeout(() => {
                    const input = this.$refs.inlineInput;
                    if (!input) {
                        this.suppressBlurCommit = false;
                        return;
                    }
                    input.focus();
                    // Select everything after "Act "/"Scene " so typing replaces
                    // the number and keeps the word. Songs have no prefix, so
                    // this is a no-op for them and the whole title is selected.
                    if (this.fixedPrefix) {
                        const prefixLen = this.fixedPrefix.length;
                        const end = input.value.length;
                        if (end > prefixLen) {
                            try {
                                input.setSelectionRange(prefixLen, end);
                            } catch (e) {
                                // ignore invalid selection on some browsers
                            }
                        }
                    } else {
                        try {
                            input.setSelectionRange(0, input.value.length);
                        } catch (e) {
                            // ignore invalid selection on some browsers
                        }
                    }
                    this.suppressBlurCommit = false;
                }, 0);
            });
        },
        onInlineKeydown(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.commitInlineEdit();
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                this.cancelInlineEdit();
            }
        },
        async commitInlineEdit() {
            if (this.suppressBlurCommit) {
                return;
            }
            // No re-prepending. The type is drawn from marker.type, so the label
            // stores the title and nothing else -- which is what stops the two
            // creation paths diverging.
            const trimmed = (this.editLabel || '').trim();
            if (!trimmed) {
                this.cancelInlineEdit(true);
                return;
            }
            this.inlineEditing = false;
            this.keepEditing = false;
            this.editing = false;
            this.$emit('updateMarker', {
                ...this.marker,
                label: trimmed
            });
            this.$emit('inlineEditDone');
        },
        cancelInlineEdit(deleteIfEmpty) {
            this.inlineEditing = false;
            this.keepEditing = false;
            this.suppressBlurCommit = false;
            this.editing = false;
            if (deleteIfEmpty && !(this.marker.label || '').trim()) {
                const id = this.marker.markerId || this.marker.id;
                this.$emit('deleteMarker', id);
            }
            this.$emit('inlineEditDone');
        },
        onDrag(x, y) {
            this.marker.x = x;
            this.marker.y = y;
            this.dragging = true;
        },
        onDragStop(x, y) {
            const marker = {
                ...this.marker,
                x,
                y
            };
            this.dragging = false;
            this.$emit('updateMarker', marker);
            this.onDeactivate();
        },
        // Pressing a mark picks it for Cmd-C, as pressing a cue does (views/project.vue, lib/cue-paste.js).
        selectForCopy() {
            NavBus.$emit('structure-mark-selected', this.marker);
        },
        selectMarker() {
            if (!this.editable || this.locked) {
                return;
            }
            this.editing = true;
            this.$emit('activated');
        },
        onActivate() {
            if (!this.editable || this.locked) {
                return;
            }
            this.editing = true;
            this.$emit('activated');
        },
        onDeactivate() {
            if (this.inlineEditing || this.keepEditing) {
                return;
            }
            this.closeActionBar();
        },
        closeActionBar() {
            this.editing = false;
            this.$emit('deactivated');
        },
        onDocumentPointerDown(e) {
            if (this.inlineEditing || this.keepEditing) {
                return;
            }
            if (this.$el && this.$el.contains(e.target)) {
                return;
            }
            this.closeActionBar();
        },
        onMarkerEditClick() {
            if (this.inlineEditing) {
                return;
            }
            this.suppressBlurCommit = true;
            this.editing = true;
            this.beginInlineEdit();
        },
        onMarkerDeleteClick() {
            this.keepEditing = true;
            this.$refs.deleteDialog.open();
        },
        async onDeleteConfirm(type) {
            this.keepEditing = false;
            if (type === 'ok') {
                const id = this.marker.markerId || this.marker.id;
                this.$emit('deleteMarker', id);
            }
            this.closeActionBar();
        }
    }
};
</script>

<style lang="scss">
/* Song type glyph. Sits beside the label, sized against the marker's own font so
   it tracks zoom with everything else. currentColor, so it inherits the type
   colour (dark ink on the pale song fill) without a second rule. */
/* Song note. Slightly larger than the label text and slightly faded, so it reads
   as a mark of type rather than as part of the title. em-sized, so it tracks the
   marker's font and therefore zoom. */
.ql-script-marker-type-glyph {
    flex: none;
    font-size: 1.1em;
    line-height: 1;
    opacity: .85;
    margin-right: 3px;
    user-select: none;
}

.ql-script-marker {
    border-radius: 4px;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px 2px 6px;
    z-index: 100 !important;
    cursor: move;
    pointer-events: fill;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    /* Actions sit above the mark — keep visible; body clips label text */
    overflow: visible !important;
    max-width: 100%;
}
/* Dark mode intentionally has NO fill override: the per-type colour set in
   markerStyle carries through. A rule here previously forced #424242 with
   !important, which beat the inline style — so removing the flattening in
   markerStyle alone would not have been enough, the marks would still have
   gone grey. The .show-md-dark-mode class stays bound as a hook if dark mode
   later needs a separation treatment. */
.ql-script-marker-body {
    flex: 1 1 auto;
    width: 100%;
    max-width: 100%;
    min-width: 0; /* allow flex child to shrink so long text wraps inside the box */
    height: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
    box-sizing: border-box;
}
.ql-script-marker.ql-script-marker--multiline {
    align-items: stretch;
}
.ql-script-marker.ql-script-marker--multiline .ql-script-marker-body {
    align-items: flex-start;
}
.ql-script-marker.ql-script-marker-locked {
    cursor: default;
}
.ql-script-marker.ql-script-marker-editing {
    z-index: 110 !important;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85);
}
.ql-script-marker-label {
    pointer-events: auto;
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    cursor: pointer;
    color: inherit;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: clip;
}
.ql-script-marker--multiline .ql-script-marker-label {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    overflow: hidden;
}
.ql-script-marker-inline-input {
    flex: 1 1 auto;
    min-width: calc(48px * var(--script-marker-scale, 1));
    width: 100%;
    border: none;
    background: transparent;
    color: inherit;
    font-size: inherit;
    font-weight: 600;
    outline: none;
    padding: 0;
    margin: 0;
    box-sizing: border-box;
}
.ql-script-marker-actions {
    position: absolute;
    top: calc(-22px * var(--script-marker-scale, 1));
    right: 0;
    display: flex;
    gap: calc(2px * var(--script-marker-scale, 1));
    background: rgba(0, 0, 0, 0.75);
    border-radius: calc(3px * var(--script-marker-scale, 1));
    padding: calc(1px * var(--script-marker-scale, 1)) calc(2px * var(--script-marker-scale, 1));
}
.ql-script-marker-action {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    color: #fff;
}
.ql-script-marker-action .md-icon {
    font-size: calc(16px * var(--script-marker-scale, 1));
}
</style>


// WEBPACK FOOTER //
// script-marker.vue?80dcf20e